import { Box, Typography, Paper } from '@mui/material';

import { ReactComponent as InfoIcon } from '../../assets/img/info-icon.svg';
import { RadarChart } from '../Graphs/RadarChart';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorState } from '../common/ErrorState';
import { NoDataState } from '../common/NoDataState';

type radarDataOptions = {
  [key: string]: number;
};

const yaxisOptions = {
  labels: {
    show: true,
    style: {
      fontSize: '12px',
    },
    offsetX: 2.5,
    offsetY: 2,
  },
};

const directions = ['N', 'N/E', 'E', 'S/E', 'S', 'S/W', 'W', 'N/W'];

export const WindDirectionRadarChart = (props: any) => {
  const { isLoading, isError, error, hasData } = props;
  const data: radarDataOptions = {};
  if (props.data) {
    directions.forEach((element: string) => {
      if (!(element in props.data)) {
        data[element] = 0;
      } else {
        data[element] = props.data[element];
      }
    });
  }

  // Show loading state
  if (isLoading) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <LoadingSpinner 
          message="Loading wind direction data..." 
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <ErrorState 
          title="Failed to load wind direction data"
          error={error}
          height="800px"
          showBackground={false}
          onRetry={props.onRetry}
        />
      </Paper>
    );
  }

  // Show no data state
  if (!hasData) {
    return (
      <Paper 
        className="custom-chart"
        sx={{ 
          minWidth: '1000px', 
          maxWidth: '100%', 
          minHeight: '800px', 
          marginBottom: '7px', 
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          overflow: 'hidden',
        }}>
        <NoDataState 
          title="No wind direction data available"
          message="No wind direction data found for the selected date range and filters. Try adjusting your selection criteria."
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  return (
    <Paper 
      className="custom-chart"
      sx={{ 
        minWidth: '1000px', 
        maxWidth: '100%', 
        minHeight: '800px', 
        marginBottom: '7px', 
        padding: '20px',
        borderRadius: '16px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(229, 231, 235, 0.8)',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '23px',
          padding: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            alignItems: 'center',
            paddingTop: '7px',
          }}
        >
          <InfoIcon />
          <Box
            sx={{
              borderLeft: '2px solid #45009C',
              height: '24px',
            }}
          ></Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ color: '#17052D' }} variant="h6">
            Average daily number of birds per wind direction
          </Typography>
          <Typography
            sx={{
              color: '#8B8296',
              fontSize: '14px',
              paddingTop: '10px',
              maxWidth: '600px',
            }}
            variant="body2"
          >
            This graph shows how many birds are detected when winds are blowing
            from the different compass directions during the selected date range
          </Typography>
        </Box>
      </Box>
      <RadarChart data={data} id={props.id} yaxisOptions={yaxisOptions} />
    </Paper>
  );
};
